import { Button, Center, Container, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import {
  ClosedNotice,
  ConnectionBadge,
  ErrorBanner,
  RejectedNotice,
} from '../components/ConnectionBanner.js';
import { useWakeLock } from '../hooks/useWakeLock.js';
import { navigate } from '../routing.js';
import { useStore } from '../store.js';
import { getToken } from '../token.js';
import { ActionBar } from './ActionBar.js';
import { JoinScreen } from './JoinScreen.js';
import { TableSummary } from './TableSummary.js';

/**
 * プレイヤー端末（要件12.2）。
 *
 * スマホの縦向き、片手の親指操作を前提にする。自分の番のアクション入力と卓状況の閲覧
 * だけを担い、卓の運営操作は持たない。
 */
export function PlayerApp({ roomCode }: { roomCode: string }): React.ReactElement {
  const connect = useStore((s) => s.connect);
  const send = useStore((s) => s.send);
  const view = useStore((s) => s.view);
  const status = useStore((s) => s.status);
  const error = useStore((s) => s.error);
  const clearError = useStore((s) => s.clearError);
  const [leaveOpen, setLeaveOpen] = useState(false);

  useEffect(() => {
    connect({ roomCode, token: getToken(), role: 'PLAYER' });
  }, [connect, roomCode]);

  const seated = view !== null && view.you.seatNo !== null;
  // 着席中は画面の自動スリープを抑止する（要件5.5）。
  useWakeLock(seated);

  if (status === 'roomClosed' || status === 'rejected') {
    return (
      <Container size="sm" pt="xl" className="safe-bottom">
        <Stack>
          {status === 'roomClosed' ? <ClosedNotice /> : <RejectedNotice roomCode={roomCode} />}
          <Button onClick={() => navigate('/')}>入口へ戻る</Button>
        </Stack>
      </Container>
    );
  }

  if (view === null) {
    return (
      <Center className="fill-height">
        <Stack align="center">
          <Loader />
          <Text c="dimmed">卓に接続中…</Text>
        </Stack>
      </Center>
    );
  }

  const canLeave = view.hand === null || view.hand.street === 'COMPLETE';

  return (
    <Stack gap={0} className="full-height" justify="space-between">
      <Stack gap={0}>
        <Group justify="space-between" px="md" pt="sm">
          <Text size="sm" c="dimmed">
            ルーム {view.code}
          </Text>
          <Group gap="xs">
            <ConnectionBadge status={status} />
            {seated && (
              <Button size="compact-xs" variant="subtle" onClick={() => setLeaveOpen(true)}>
                退席
              </Button>
            )}
          </Group>
        </Group>

        {error !== null && (
          <div style={{ padding: 'var(--mantine-spacing-md)' }}>
            <ErrorBanner error={error} onClose={clearError} />
          </div>
        )}

        {seated ? <TableSummary view={view} /> : <JoinScreen view={view} />}
      </Stack>

      {seated && <ActionBar view={view} />}

      <Modal opened={leaveOpen} onClose={() => setLeaveOpen(false)} title="卓から退席する" centered>
        <Stack>
          <Text>
            {
              '退席すると席とスタックが失われます。休憩なら退席せず、そのまま画面を閉じてください。同じ端末で開き直せば同じ席に戻れます。'
            }
          </Text>
          {!canLeave && (
            <Text c="yellow">ハンド進行中は退席できません。終わるまでお待ちください。</Text>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setLeaveOpen(false)}>
              やめる
            </Button>
            <Button
              color="red"
              disabled={!canLeave}
              onClick={() => {
                send({ type: 'seat:leave' });
                setLeaveOpen(false);
              }}
            >
              退席する
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
